import React from 'react';
import type { Transition } from 'framer-motion';
import { ArrowsInSimpleIcon, ArrowUpIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { PromptInput } from '@/components/PromptInput';
import { QueryPanelBody } from './QueryPanelBody';
import type { QueryPanelState } from '../hooks';

interface ExpandedViewProps {
    state: QueryPanelState;
    compact?: boolean;
    prefix?: string;
    filtersWrapper?: (filters: React.ReactNode) => React.ReactNode;
    actionsWrapper?: (actions: React.ReactNode) => React.ReactNode;
    inputLayoutProps?: {
        searchIconLayoutId: string;
        searchFieldLayoutId: string;
        searchSharedLayoutTransition: Transition;
        onSearchFocus: () => void;
    };
}

export function ExpandedView({
    state,
    compact,
    prefix,
    filtersWrapper,
    actionsWrapper,
    inputLayoutProps,
}: ExpandedViewProps) {
    const { t } = useTranslation(I18nNamespace.QUERIES);

    return (
        <QueryPanelBody
            state={state}
            inputSlot={
                <PromptInput
                    value={state.searchQuery}
                    onValueChange={state.setSearchQuery}
                    onKeyDown={state.handleKeyDown}
                    onPaste={state.handlePaste}
                    inputRef={state.inputRef}
                    prefix={prefix}
                    completions={state.recentQueries}
                    suggestion={state.suggestion}
                    {...inputLayoutProps}
                />
            }
            filtersWrapper={filtersWrapper}
            actionsWrapper={actionsWrapper}
            filterTrailingSlot={
                compact ? (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="medium"
                                shape="square"
                                onClick={state.handleMinimize}
                                className="size-8 min-w-8 shrink-0 p-0 transition-transform duration-150 ease-out-expo active:scale-[0.97]"
                                aria-label={t('input.minimize')}
                                leadingIcon={
                                    <ArrowsInSimpleIcon className="size-4" />
                                }
                            />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                            {t('input.minimizeTooltip')}
                        </TooltipContent>
                    </Tooltip>
                ) : undefined
            }
            actionsTrailingSlot={
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            type="button"
                            variant="primary"
                            shape="square"
                            size="medium"
                            onClick={state.handleSearch}
                            disabled={!state.canSearch}
                            className="size-8 min-w-8 p-0 active:scale-[0.97]"
                            aria-label={t('input.submitTooltip')}
                            leadingIcon={<ArrowUpIcon className="size-4" />}
                        />
                    </TooltipTrigger>
                    <TooltipContent side="top">
                        {t('input.submitTooltip')}
                    </TooltipContent>
                </Tooltip>
            }
        />
    );
}
